import { circle, createIcon, dot, hole, path, rect } from './create-icon';

/*
 * Набор иконок сайта. Стрелки, шевроны, крестики и логотипы брендов остаются из
 * Phosphor — их здесь нет намеренно.
 *
 * Сетка 24×24, рабочее поле ~20×20, линия 1.5. Слои см. в create-icon.tsx:
 * body заливается в сплошном варианте, detail вырезается, line — всегда линия.
 */

const pin = 'M12 21.5c-1.2 0-7.5-5.6-7.5-11.2A7.5 7.5 0 0 1 19.5 10.3c0 5.6-6.3 11.2-7.5 11.2z';
const bubble =
  'M12 21.5c5.2 0 9.5-4.3 9.5-9.5S17.2 2.5 12 2.5 2.5 6.8 2.5 12c0 1.6.4 3.1 1.1 4.4l-.9 3.7c-.1.6.4 1.1 1 1l3.7-.9c1.3.7 2.8 1.1 4.4 1.1z';
const handset =
  'M4.1 3.9 6.4 2.8c.8-.4 1.8-.1 2.2.7l1.6 3.2c.3.7.2 1.5-.4 2L8.2 10.1c1.2 2.5 3.2 4.5 5.7 5.7l1.4-1.6c.5-.6 1.3-.7 2-.4l3.2 1.6c.8.4 1.1 1.4.7 2.2l-1.1 2.3c-.5 1-1.6 1.6-2.7 1.4C10.1 20.1 3.9 13.9 2.7 6.6c-.2-1.1.4-2.2 1.4-2.7z';
const shield =
  'M12 21.5c-.3 0-.5 0-.8-.2-3.9-1.8-6.7-5.4-6.7-9.7V6.8c0-.8.5-1.5 1.2-1.8l5.5-2.2c.5-.2 1.1-.2 1.6 0L18.3 5c.7.3 1.2 1 1.2 1.8v4.8c0 4.3-2.8 7.9-6.7 9.7-.3.2-.5.2-.8.2z';
const box = 'M12 2.8 20 6.9v10.2L12 21.2 4 17.1V6.9z';
const boxEdges = 'M4 6.9l8 4.1 8-4.1M12 11v10.2';
const flame = (x: number) =>
  `M${x} 6.2c-.6 0-1-.4-1-1 0-.6 1-1.7 1-1.7s1 1.1 1 1.7c0 .6-.4 1-1 1z`;

// ── Базовые ─────────────────────────────────────────────────────────────

export const CheckIcon = /* @__PURE__ */ createIcon('check', { line: ['M5 13l4 4L19 7'] });

export const PlusIcon = /* @__PURE__ */ createIcon('plus', { line: ['M12 5v14M5 12h14'] });

export const MinusIcon = /* @__PURE__ */ createIcon('minus', { line: ['M5 12h14'] });

export const SearchIcon = /* @__PURE__ */ createIcon('search', {
  body: [circle(11, 11, 7.5)],
  line: ['M16.3 16.3 20.5 20.5'],
});

export const LinkIcon = /* @__PURE__ */ createIcon('link', {
  line: [
    'M10.5 13.5a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1 1',
    'M13.5 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1-1',
  ],
});

export const SpinnerIcon = /* @__PURE__ */ createIcon('spinner', {
  line: ['M12 3a9 9 0 1 0 9 9'],
});

// ── Статусы ─────────────────────────────────────────────────────────────

export const CheckCircleIcon = /* @__PURE__ */ createIcon('check-circle', {
  body: [circle(12, 12, 10)],
  detail: ['M8 12.5l2.5 2.5L16 9'],
});

export const InfoIcon = /* @__PURE__ */ createIcon('info', {
  body: [circle(12, 12, 10)],
  detail: ['M12 11.5v5', dot(12, 8)],
});

export const AlertCircleIcon = /* @__PURE__ */ createIcon('alert-circle', {
  body: [circle(12, 12, 10)],
  detail: ['M12 7.5V13', dot(12, 16.5)],
});

export const AlertTriangleIcon = /* @__PURE__ */ createIcon('alert-triangle', {
  body: [
    'M10.27 4.02c.77-1.35 2.69-1.35 3.46 0l7.46 13.05c.77 1.35-.2 3.03-1.75 3.03H4.56c-1.55 0-2.52-1.68-1.75-3.03z',
  ],
  detail: ['M12 9v4.5', dot(12, 16.8)],
});

export const QuestionIcon = /* @__PURE__ */ createIcon('question', {
  body: [circle(12, 12, 10)],
  detail: ['M9.75 9.5a2.25 2.25 0 1 1 3.1 2.08c-.5.2-.85.68-.85 1.22v.7', dot(12, 17)],
});

// ── Время ───────────────────────────────────────────────────────────────

export const ClockIcon = /* @__PURE__ */ createIcon('clock', {
  body: [circle(12, 12, 10)],
  detail: ['M12 7.5V12l3 2'],
});

export const TimerIcon = /* @__PURE__ */ createIcon('timer', {
  body: [circle(12, 13.5, 8)],
  detail: ['M12 10v3.5l2 1.5'],
  line: ['M9.5 2.5h5M12 2.5v3M18.2 7.3l1.3-1.3'],
});

export const CalendarIcon = /* @__PURE__ */ createIcon('calendar', {
  body: [rect(3, 4.5, 18, 17, 3)],
  detail: ['M3 9.5h18', dot(8, 13.5), dot(12, 13.5), dot(16, 13.5), dot(8, 17), dot(12, 17)],
  line: ['M8 2.5v4M16 2.5v4'],
});

// ── Места и доставка ────────────────────────────────────────────────────

export const MapPinIcon = /* @__PURE__ */ createIcon('map-pin', {
  body: [pin],
  detail: [hole(circle(12, 10.3, 2.75))],
});

export const MapPinLineIcon = /* @__PURE__ */ createIcon('map-pin-line', {
  body: ['M12 19c-1 0-6.5-4.8-6.5-9.6a6.5 6.5 0 0 1 13 0c0 4.8-5.5 9.6-6.5 9.6z'],
  detail: [hole(circle(12, 9.4, 2.4))],
  line: ['M5 21.5h14'],
});

export const MapIcon = /* @__PURE__ */ createIcon('map', {
  body: ['M3.5 6.3 9 4l6 2.3 5.5-2.3v13.7L15 20l-6-2.3L3.5 20z'],
  detail: ['M9 4v13.7M15 6.3V20'],
});

export const NavigationIcon = /* @__PURE__ */ createIcon('navigation', {
  body: ['M20.5 3.5 3.8 10.1c-.9.4-.8 1.7.1 1.9l6.6 1.5 1.5 6.6c.2.9 1.5 1 1.9.1z'],
});

export const CrosshairIcon = /* @__PURE__ */ createIcon('crosshair', {
  body: [circle(12, 12, 8)],
  detail: [hole(circle(12, 12, 2.5))],
  line: ['M12 2v3M12 19v3M2 12h3M19 12h3'],
});

export const RouteIcon = /* @__PURE__ */ createIcon('route', {
  body: [circle(4.5, 19, 2), circle(19.5, 5, 2)],
  line: ['M6.5 19h8a3.5 3.5 0 0 0 0-7h-5a3.5 3.5 0 0 1 0-7h8'],
});

export const HomeIcon = /* @__PURE__ */ createIcon('home', {
  body: [
    'M3.5 10.2c0-.8.4-1.5 1-2L10.3 3.8c1-.8 2.4-.8 3.4 0l5.8 4.4c.6.5 1 1.2 1 2v8.3c0 1.7-1.3 3-3 3h-11c-1.7 0-3-1.3-3-3z',
  ],
  detail: [hole('M9.5 21.5v-4a2.5 2.5 0 0 1 5 0v4')],
});

export const BuildingsIcon = /* @__PURE__ */ createIcon('buildings', {
  body: [rect(3.5, 2.5, 11, 19, 2.5), 'M14.5 8.5h3.5c1.4 0 2.5 1.1 2.5 2.5v10.5h-6z'],
  detail: ['M7.5 7h3.5M7.5 10.5h3.5M7.5 14h3.5'],
  line: ['M2 21.5h20'],
});

export const TruckIcon = /* @__PURE__ */ createIcon('truck', {
  body: [
    'M2.5 6c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2v11.5H4c-.8 0-1.5-.7-1.5-1.5z',
    'M14.5 8.5h3.2c.6 0 1.2.3 1.5.8l2 3c.2.3.3.6.3.9V16c0 .8-.7 1.5-1.5 1.5h-5.5z',
  ],
  over: [circle(7, 18, 2), circle(17.5, 18, 2)],
});

export const PackageIcon = /* @__PURE__ */ createIcon('package', {
  body: [box],
  detail: [boxEdges, 'M8 4.9l8 4.1'],
});

export const CubeIcon = /* @__PURE__ */ createIcon('cube', {
  body: [box],
  detail: [boxEdges],
});

// ── Связь ───────────────────────────────────────────────────────────────

export const PhoneIcon = /* @__PURE__ */ createIcon('phone', { body: [handset] });

export const PhoneCallIcon = /* @__PURE__ */ createIcon('phone-call', {
  body: [handset],
  line: ['M14.5 2.5a7 7 0 0 1 7 7M14 6a4 4 0 0 1 4 4'],
});

export const MailIcon = /* @__PURE__ */ createIcon('mail', {
  body: [rect(2.5, 4.5, 19, 15, 3)],
  detail: ['M6.5 9l4 2.9c.9.7 2.1.7 3 0l4-2.9'],
});

export const SendIcon = /* @__PURE__ */ createIcon('send', {
  body: ['M21 3 3.6 9.1c-.8.3-.9 1.4-.1 1.8l6.3 3.3 3.3 6.3c.4.8 1.5.7 1.8-.1z'],
  detail: ['M9.8 14.2 14 10'],
});

export const ChatIcon = /* @__PURE__ */ createIcon('chat', { body: [bubble] });

export const ChatDotsIcon = /* @__PURE__ */ createIcon('chat-dots', {
  body: [bubble],
  detail: [dot(8, 12), dot(12, 12), dot(16, 12)],
});

export const ChatTextIcon = /* @__PURE__ */ createIcon('chat-text', {
  body: [bubble],
  detail: ['M8 10h8M8 14h5'],
});

export const BellIcon = /* @__PURE__ */ createIcon('bell', {
  body: [
    'M4.5 16c.9-.8 1.3-2 1.3-3.2V9.5a6.2 6.2 0 0 1 12.4 0v3.3c0 1.2.4 2.4 1.3 3.2.4.4.1 1-.4 1H4.9c-.5 0-.8-.6-.4-1z',
  ],
  line: ['M9.8 19.5a2.3 2.3 0 0 0 4.4 0'],
});

// ── Акции и лояльность ──────────────────────────────────────────────────

export const StarIcon = /* @__PURE__ */ createIcon('star', {
  body: [
    'M11.1 3.3c.4-.8 1.4-.8 1.8 0l2.1 4.3 4.7.7c.8.1 1.1 1.1.6 1.7l-3.4 3.3.8 4.7c.1.8-.7 1.4-1.5 1.1L12 16.8l-4.2 2.3c-.7.4-1.6-.3-1.5-1.1l.8-4.7-3.4-3.3c-.6-.6-.3-1.6.6-1.7l4.7-.7z',
  ],
});

export const SparkleIcon = /* @__PURE__ */ createIcon('sparkle', {
  body: ['M11 3c.7 5 2.5 6.8 7.5 7.5-5 .7-6.8 2.5-7.5 7.5-.7-5-2.5-6.8-7.5-7.5 5-.7 6.8-2.5 7.5-7.5z'],
  line: ['M18.5 16v4M16.5 18h4'],
});

export const GiftIcon = /* @__PURE__ */ createIcon('gift', {
  body: [rect(3.5, 8, 17, 4, 1.5), 'M5 12h14v7c0 1.4-1.1 2.5-2.5 2.5h-9C6.1 21.5 5 20.4 5 19z'],
  detail: ['M12 8v13.5M5 12h14'],
  line: ['M12 8C11 5 8 3.5 7 5.2 6.2 6.7 8 8 12 8zm0 0c1-3 4-4.5 5-2.8.8 1.5-1 2.8-5 2.8z'],
});

export const HeartIcon = /* @__PURE__ */ createIcon('heart', {
  body: [
    'M12 20.5c-.4 0-.8-.1-1.1-.4C6.5 16.4 2.5 13 2.5 8.8 2.5 6 4.6 3.5 7.5 3.5c1.8 0 3.3.9 4.5 2.5 1.2-1.6 2.7-2.5 4.5-2.5 2.9 0 5 2.5 5 5.3 0 4.2-4 7.6-8.4 11.3-.3.3-.7.4-1.1.4z',
  ],
});

export const MedalIcon = /* @__PURE__ */ createIcon('medal', {
  body: [circle(12, 14.5, 6.5)],
  detail: [circle(12, 14.5, 2.75)],
  line: [
    'M8.5 8.8 6.5 3.5c-.2-.5.2-1 .7-1h2.6c.4 0 .7.2.9.6L12 6.5l1.3-3.4c.2-.4.5-.6.9-.6h2.6c.5 0 .9.5.7 1l-2 5.3',
  ],
});

export const TrophyIcon = /* @__PURE__ */ createIcon('trophy', {
  body: ['M7 3.5h10V9a5 5 0 0 1-10 0z', 'M8.5 20.5c0-1.7 1.3-3 3-3h1c1.7 0 3 1.3 3 3v1h-7z'],
  line: ['M17 5.5h1.5a2 2 0 0 1 0 4H17M7 5.5H5.5a2 2 0 0 0 0 4H7M12 14v3.5'],
});

export const CrownIcon = /* @__PURE__ */ createIcon('crown', {
  body: [
    'M4 17.5 2.8 8.3c-.1-.7.7-1.1 1.2-.7l3.8 3.2 3.5-5.9c.3-.5 1.1-.5 1.4 0l3.5 5.9 3.8-3.2c.5-.4 1.3 0 1.2.7L20 17.5z',
  ],
  line: ['M5 21h14'],
});

export const DiamondIcon = /* @__PURE__ */ createIcon('diamond', {
  body: [
    'M6.6 4h10.8c.5 0 1 .3 1.3.7l2.6 4c.3.5.3 1.2-.1 1.7l-8.2 9.4c-.5.6-1.4.6-1.9 0l-8.2-9.4c-.4-.5-.4-1.2-.1-1.7l2.6-4c.3-.4.8-.7 1.2-.7z',
  ],
  detail: ['M3 9.5h18M9.5 4 8 9.5l4 10 4-10L14.5 4'],
});

export const TicketIcon = /* @__PURE__ */ createIcon('ticket', {
  body: [
    'M3 8.5V7c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v1.5a3.5 3.5 0 0 0 0 7V17c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2v-1.5a3.5 3.5 0 0 0 0-7z',
  ],
  detail: ['M14.5 5v2M14.5 11v2M14.5 17v2'],
});

export const TagIcon = /* @__PURE__ */ createIcon('tag', {
  body: [
    'M3 5v5.2c0 .5.2 1 .6 1.4l8.8 8.8c.8.8 2 .8 2.8 0l5.2-5.2c.8-.8.8-2 0-2.8L11.6 3.6c-.4-.4-.9-.6-1.4-.6H5c-1.1 0-2 .9-2 2z',
  ],
  detail: [hole(circle(7.5, 7.5, 1.5))],
});

export const PercentIcon = /* @__PURE__ */ createIcon('percent', {
  body: [circle(7, 7, 2.5), circle(17, 17, 2.5)],
  line: ['M19 5 5 19'],
});

export const GamepadIcon = /* @__PURE__ */ createIcon('gamepad', {
  body: [
    'M7.5 6.5h9a5 5 0 0 1 4.8 3.8l.9 4.9c.3 1.9-1.1 3.8-3.1 3.8-1 0-2-.5-2.5-1.4L15.5 16h-7l-1.1 1.6c-.5.9-1.5 1.4-2.5 1.4-2 0-3.4-1.9-3.1-3.8l.9-4.9A5 5 0 0 1 7.5 6.5z',
  ],
  detail: ['M6.5 10.5v3M5 12h3', dot(16, 11), dot(18, 13)],
});

// ── Деньги ──────────────────────────────────────────────────────────────

export const CoinsIcon = /* @__PURE__ */ createIcon('coins', {
  body: [circle(15, 9, 6)],
  over: [circle(9.5, 14.5, 6.5)],
  overDetail: [circle(9.5, 14.5, 3.25)],
});

export const RubleIcon = /* @__PURE__ */ createIcon('ruble', {
  body: [circle(12, 12, 10)],
  detail: ['M10 17.5V7h3a2.75 2.75 0 0 1 0 5.5H8.5M8.5 15h5'],
});

export const CashIcon = /* @__PURE__ */ createIcon('cash', {
  body: [rect(2.5, 6, 19, 12, 2.5)],
  detail: [circle(12, 12, 2.5), dot(5.5, 12), dot(18.5, 12)],
});

export const CreditCardIcon = /* @__PURE__ */ createIcon('credit-card', {
  body: [rect(2.5, 5, 19, 14, 3)],
  detail: ['M2.5 9.5h19M6.5 15h3'],
});

export const ReceiptIcon = /* @__PURE__ */ createIcon('receipt', {
  body: [
    'M7.5 2.5h9a2 2 0 0 1 2 2V21l-2.17-1.5L14.17 21 12 19.5 9.83 21l-2.16-1.5L5.5 21V4.5a2 2 0 0 1 2-2z',
  ],
  detail: ['M9 7.5h6M9 11h6M9 14.5h3.5'],
});

// ── Еда ─────────────────────────────────────────────────────────────────

export const FireIcon = /* @__PURE__ */ createIcon('fire', {
  body: [
    'M12 21.5c4 0 7-2.8 7-6.8 0-3.7-2.3-6.4-4.2-8.4-.5-.5-1.3-.2-1.3.5 0 1.3-.6 2.6-1.6 3.2C11.8 7 10.6 4.2 8.5 2.8c-.5-.3-1.1 0-1.1.6C7.2 7 5 9.4 5 14.7c0 4 3 6.8 7 6.8z',
  ],
  detail: [
    'M12 21.5c-1.7 0-3-1.2-3-2.9 0-1.9 1.6-3.2 3-4.6 1.4 1.4 3 2.7 3 4.6 0 1.7-1.3 2.9-3 2.9z',
  ],
});

export const LeafIcon = /* @__PURE__ */ createIcon('leaf', {
  body: ['M4.5 19.5C4 11 8.5 4.5 20 4c.5 11.5-6 16-14.5 15.5z'],
  detail: ['M6 18c3-4.5 6-7 9-8.5'],
  line: ['M3.5 20.5 6 18'],
});

export const DropIcon = /* @__PURE__ */ createIcon('drop', {
  body: [
    'M12 21.5c-3.9 0-7-3-7-6.9 0-4.3 4.4-9 6.2-11.7.4-.5 1.2-.5 1.6 0C14.6 5.6 19 10.3 19 14.6c0 3.9-3.1 6.9-7 6.9z',
  ],
  detail: ['M15.5 14.5a3.5 3.5 0 0 1-3.5 3.5'],
});

export const ChiliIcon = /* @__PURE__ */ createIcon('chili', {
  body: [
    'M17.5 8.5c1.5 1.5 1.3 4.8-1 7.6-2.8 3.4-7.3 5.4-11.6 5.4-.9 0-1.2-1.1-.5-1.6 3.4-2.3 5.7-5.5 6.7-9.3.6-2.2 4.9-3.6 6.4-2.1z',
  ],
  line: ['M16 7.2c.3-1.9 1.7-3.6 4-4.2'],
});

export const BowlIcon = /* @__PURE__ */ createIcon('bowl', {
  body: ['M3 11.5h18c0 4.7-3.8 8.5-8.5 8.5h-1C6.8 20 3 16.2 3 11.5z'],
  line: ['M13.5 9.5 20 3M10.5 9.5l6-6.5M8.5 21.5h7'],
});

export const CutleryIcon = /* @__PURE__ */ createIcon('cutlery', {
  body: ['M17.5 3c-1.9 2.2-2.9 6.2-2.8 11.5h2.8z'],
  line: ['M6.5 3v5.5a2 2 0 0 0 4 0V3M8.5 3v18M17.5 14.5V21'],
});

export const CakeIcon = /* @__PURE__ */ createIcon('cake', {
  body: ['M4 21.5v-7c0-1.7 1.3-3 3-3h10c1.7 0 3 1.3 3 3v7z', flame(8), flame(12), flame(16)],
  detail: [
    'M4 16c1.3 0 1.3 1 2.7 1s1.3-1 2.6-1 1.4 1 2.7 1 1.3-1 2.7-1 1.3 1 2.6 1 1.3-1 2.7-1',
  ],
  line: ['M8 11.5v-3M12 11.5v-3M16 11.5v-3M2.5 21.5h19'],
});

export const DumbbellIcon = /* @__PURE__ */ createIcon('dumbbell', {
  body: [rect(2, 9, 2.5, 6, 1.2), rect(5.5, 6, 3.5, 12, 1.5), rect(15, 6, 3.5, 12, 1.5), rect(19.5, 9, 2.5, 6, 1.2)],
  line: ['M9 12h6M4.5 12h1M18.5 12h1'],
});

export const ScaleIcon = /* @__PURE__ */ createIcon('scale', {
  body: [rect(3.5, 3.5, 17, 17, 4.5)],
  detail: ['M8 11.5a4 4 0 0 1 8 0M12 11.5l1.9-2.3'],
});

export const ThermometerIcon = /* @__PURE__ */ createIcon('thermometer', {
  body: ['M9.5 14.2V5a2.5 2.5 0 0 1 5 0v9.2a4.5 4.5 0 1 1-5 0z'],
  detail: ['M12 9v8'],
});

// ── Корзина и заказ ─────────────────────────────────────────────────────

export const BagIcon = /* @__PURE__ */ createIcon('bag', {
  body: [
    'M4.4 8.9c.1-1.3 1.2-2.4 2.5-2.4h10.2c1.3 0 2.4 1 2.5 2.4l.8 10c.1 1.4-1 2.6-2.5 2.6H6.1c-1.5 0-2.6-1.2-2.5-2.6z',
  ],
  detail: [dot(8.5, 10), dot(15.5, 10)],
  line: ['M8.5 6.5V6a3.5 3.5 0 0 1 7 0v.5'],
});

export const CartIcon = /* @__PURE__ */ createIcon('cart', {
  body: [
    'M5.9 6.5h13.9c.8 0 1.4.7 1.2 1.5l-1.5 6.1c-.2.8-1 1.4-1.9 1.4H9.3c-.9 0-1.7-.6-1.9-1.5z',
    circle(9.5, 19.5, 1.5),
    circle(17, 19.5, 1.5),
  ],
  line: ['M2.5 3.5h1.6c.7 0 1.3.5 1.5 1.2l.3 1.8'],
});

// ── Аллергены ───────────────────────────────────────────────────────────

export const FishIcon = /* @__PURE__ */ createIcon('fish', {
  body: [
    'M2.5 12c2.2-3.3 5.3-5 8.7-5 2.4 0 4.6 1 6.3 2.7l4-2.2v9l-4-2.2c-1.7 1.7-3.9 2.7-6.3 2.7-3.4 0-6.5-1.7-8.7-5z',
  ],
  detail: [dot(7, 11), 'M11 9.5c.7 1.6.7 3.4 0 5'],
});

export const ShrimpIcon = /* @__PURE__ */ createIcon('shrimp', {
  body: [
    'M17.5 5.5C11.5 5.5 6.5 9.4 6.5 14.3c0 2.7 1.4 4.9 3.6 6l1.8-3.2c-1.1-.6-1.7-1.6-1.7-2.8 0-2.6 3.2-4.8 7.3-4.8z',
  ],
  detail: ['M9.8 8.1l2.5 2.8M6.6 14.3h3.6', dot(15.5, 7.5)],
  line: ['M17.5 6.5c1.5-1.4 3-2 4.5-2M17.5 8.5c1.6-.4 3.1-.2 4.5.6M11 18.7l-2.7 2.8M11 18.7l2.5 2.6'],
});

export const ShellIcon = /* @__PURE__ */ createIcon('shell', {
  body: ['M12 20 3.8 11.4a8.5 8.5 0 0 1 16.4 0z'],
  detail: ['M12 17.5V6.5M12 17.5 8.3 7.4M12 17.5l3.7-10.1'],
  line: ['M9.5 21.5h5'],
});

export const WheatIcon = /* @__PURE__ */ createIcon('wheat', {
  body: [
    'M12 8.5c-1.2-1-1.8-2.2-1.8-3.5 0-1.1.6-2 1.8-2.5 1.2.5 1.8 1.4 1.8 2.5 0 1.3-.6 2.5-1.8 3.5z',
    'M12 12.5c-2.2 0-3.5-1.3-3.5-3.5 2.2 0 3.5 1.3 3.5 3.5z',
    'M12 12.5c2.2 0 3.5-1.3 3.5-3.5-2.2 0-3.5 1.3-3.5 3.5z',
    'M12 16.5c-2.2 0-3.5-1.3-3.5-3.5 2.2 0 3.5 1.3 3.5 3.5z',
    'M12 16.5c2.2 0 3.5-1.3 3.5-3.5-2.2 0-3.5 1.3-3.5 3.5z',
  ],
  line: ['M12 16.5v5'],
});

const seed = 'M0-4c1.6 1.4 2.5 3 2.5 4.8 0 1.6-1.1 2.7-2.5 2.7s-2.5-1.1-2.5-2.7C-2.5-1.1-1.6-2.6 0-4z';

export const SesameIcon = /* @__PURE__ */ createIcon('sesame', {
  body: [
    path(seed, 'translate(8 8.5) rotate(-35)'),
    path(seed, 'translate(16.2 9.5) rotate(35)'),
    path(seed, 'translate(11.5 17) rotate(-165)'),
  ],
});

export const SoyIcon = /* @__PURE__ */ createIcon('soy', {
  body: [
    path(
      'M3.5 12A3.5 3.5 0 0 1 7 8.5h10a3.5 3.5 0 0 1 0 7H7A3.5 3.5 0 0 1 3.5 12z',
      'rotate(-45 12 12)'
    ),
  ],
  detail: [circle(8.5, 15.5, 1.6), circle(12, 12, 1.6), circle(15.5, 8.5, 1.6)],
  line: ['M18.4 5.6 20 4'],
});

export const EggIcon = /* @__PURE__ */ createIcon('egg', {
  body: ['M12 21c-3.9 0-6.5-2.9-6.5-6.9C5.5 8.9 8.4 3 12 3s6.5 5.9 6.5 11.1c0 4-2.6 6.9-6.5 6.9z'],
  detail: ['M9 14.5c0-1.9.5-3.7 1.4-5'],
});

export const MilkIcon = /* @__PURE__ */ createIcon('milk', {
  body: [
    rect(9.5, 2.5, 5, 3.5, 1),
    'M6.5 10 8.5 6h7l2 4v9.5c0 1.1-.9 2-2 2h-7c-1.1 0-2-.9-2-2z',
  ],
  detail: [
    'M6.5 10h11',
    hole('M12 13.5c-.9 1.2-1.5 2.1-1.5 2.9a1.5 1.5 0 0 0 3 0c0-.8-.6-1.7-1.5-2.9z'),
  ],
});

export const NutIcon = /* @__PURE__ */ createIcon('nut', {
  body: ['M4.5 10c0-3.3 3.4-6 7.5-6s7.5 2.7 7.5 6z', 'M6.5 10h11v1.5c0 4.4-2.4 8.4-5.5 10-3.1-1.6-5.5-5.6-5.5-10z'],
  detail: ['M4.5 10h15'],
  line: ['M12 4V2.5'],
});

export const PeanutIcon = /* @__PURE__ */ createIcon('peanut', {
  body: [
    'M12 2.5c2.6 0 4.5 2 4.5 4.6 0 1.4-.6 2.3-1.2 3.1-.4.6-.4 1.2 0 1.8.9 1 1.7 2.2 1.7 3.9 0 3-2.3 5.6-5 5.6s-5-2.6-5-5.6c0-1.7.8-2.9 1.7-3.9.4-.6.4-1.2 0-1.8-.6-.8-1.2-1.7-1.2-3.1 0-2.6 1.9-4.6 4.5-4.6z',
  ],
  detail: [dot(10.5, 6.5), dot(13.5, 8), dot(10.5, 15), dot(13.5, 16.5)],
});

// ── Оценки ──────────────────────────────────────────────────────────────

const face = [circle(12, 12, 10)];
const eyes = [dot(9, 10), dot(15, 10)];
const happyEyes = 'M7.8 10.5c.4-.9 1.1-1.4 1.9-1.4s1.5.5 1.9 1.4M12.4 10.5c.4-.9 1.1-1.4 1.9-1.4s1.5.5 1.9 1.4';

export const FaceSadIcon = /* @__PURE__ */ createIcon('face-sad', {
  body: face,
  detail: [...eyes, 'M8.5 16.5c.8-1.2 2-2 3.5-2s2.7.8 3.5 2'],
});

export const FaceNeutralIcon = /* @__PURE__ */ createIcon('face-neutral', {
  body: face,
  detail: [...eyes, 'M9 15.5h6'],
});

export const FaceSmileIcon = /* @__PURE__ */ createIcon('face-smile', {
  body: face,
  detail: [...eyes, 'M9 14.5c.8.9 1.8 1.4 3 1.4s2.2-.5 3-1.4'],
});

export const FaceHappyIcon = /* @__PURE__ */ createIcon('face-happy', {
  body: face,
  detail: [happyEyes, 'M8.5 14c.8 1.6 2 2.5 3.5 2.5s2.7-.9 3.5-2.5'],
});

export const FaceDelightIcon = /* @__PURE__ */ createIcon('face-delight', {
  body: face,
  detail: [happyEyes, hole('M8 13.5h8c0 2.2-1.8 4-4 4s-4-1.8-4-4z')],
});

// ── Погода и энергия ────────────────────────────────────────────────────

export const LightningIcon = /* @__PURE__ */ createIcon('lightning', {
  body: [
    'M13.2 2.6 5 13.2c-.4.5 0 1.3.6 1.3H11l-.9 6.2c-.1.7.8 1.1 1.2.5L19.4 10.3c.4-.5 0-1.3-.6-1.3H13.5l.9-5.9c.1-.7-.8-1.1-1.2-.5z',
  ],
});

export const SunIcon = /* @__PURE__ */ createIcon('sun', {
  body: [circle(12, 12, 4)],
  line: [
    'M12 2.5V4M12 20v1.5M4.9 4.9 6 6M18 18l1.1 1.1M2.5 12H4M20 12h1.5M4.9 19.1 6 18M18 6l1.1-1.1',
  ],
});

export const MoonIcon = /* @__PURE__ */ createIcon('moon', {
  body: ['M20.5 14.3A8.5 8.5 0 0 1 9.7 3.5a8.5 8.5 0 1 0 10.8 10.8z'],
});

// ── Люди ────────────────────────────────────────────────────────────────

export const UserIcon = /* @__PURE__ */ createIcon('user', {
  body: [
    circle(12, 7.5, 4),
    'M4.5 19.5c0-3.6 3.4-6.5 7.5-6.5s7.5 2.9 7.5 6.5c0 1.1-.9 2-2 2h-11c-1.1 0-2-.9-2-2z',
  ],
});

export const UsersIcon = /* @__PURE__ */ createIcon('users', {
  body: [
    circle(9, 7.5, 3.5),
    'M2.5 20c0-3.3 2.9-6 6.5-6s6.5 2.7 6.5 6c0 .8-.7 1.5-1.5 1.5H4c-.8 0-1.5-.7-1.5-1.5z',
  ],
  line: ['M15.5 4.3a3.5 3.5 0 0 1 0 6.4M18 14.5c2 .7 3.5 2.6 3.5 4.9 0 .9-.5 1.6-1.2 1.9'],
});

export const UserCircleIcon = /* @__PURE__ */ createIcon('user-circle', {
  body: [circle(12, 12, 10)],
  detail: [circle(12, 9.5, 3), 'M6.8 18.5c1.2-1.9 3-3 5.2-3s4 1.1 5.2 3'],
});

export const IdCardIcon = /* @__PURE__ */ createIcon('id-card', {
  body: [rect(2.5, 4.5, 19, 15, 3)],
  detail: [circle(8.5, 11, 2), 'M5.5 16c.6-1.2 1.7-2 3-2s2.4.8 3 2M14 9.5h4M14 13h3'],
});


export const HandshakeIcon = /* @__PURE__ */ createIcon('handshake', {
  body: [rect(2, 6.5, 3, 9.5, 1), rect(19, 6.5, 3, 9.5, 1)],
  line: [
    'M5 8.2l3.6-1.5c.7-.3 1.4-.3 2 .1l.9.5',
    'M19 8.5l-2.9-1.7c-.7-.4-1.5-.4-2.1 0l-3.9 2.6c-.6.4-.8 1.3-.3 1.9.4.6 1.3.8 1.9.4l1.9-1.2 4.3 4c.6.6.6 1.5 0 2.1s-1.5.6-2.1 0l-.8-.7',
    'M5 14.3l4.8 4.4c.6.5 1.5.5 2-.1.6-.6.5-1.5-.1-2l-.5-.5',
    'M11.2 16.1l1.5 1.3c.6.5 1.5.5 2-.1.6-.6.5-1.5-.1-2l-.8-.8',
  ],
});

export const HandHeartIcon = /* @__PURE__ */ createIcon('hand-heart', {
  body: [
    'M15 12.5c-.3 0-.6-.1-.8-.3-2-1.6-3.7-3-3.7-4.9 0-1.3 1-2.3 2.3-2.3.9 0 1.6.4 2.2 1.1.6-.7 1.3-1.1 2.2-1.1 1.3 0 2.3 1 2.3 2.3 0 1.9-1.7 3.3-3.7 4.9-.2.2-.5.3-.8.3z',
  ],
  line: [
    'M2.5 14h2.5l3.5 1.5h3a1.5 1.5 0 0 1 0 3H9',
    'M11.5 18.5l5.4-2.2c.9-.4 1.9.1 2.1 1 .2.7-.1 1.4-.8 1.7l-6.3 2.5c-.8.3-1.6.3-2.4 0L5 20M2.5 13.5v7',
  ],
});

export const EarIcon = /* @__PURE__ */ createIcon('ear', {
  line: [
    'M6.5 9a5.5 5.5 0 0 1 11 0c0 2.4-1.4 3.7-2.6 4.8-.9.8-1.4 1.8-1.4 3 0 2.3-1.8 4.2-4.2 4.2-1.6 0-2.9-.9-3.6-2.2',
    'M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1-.6 1.6-1.3 2',
  ],
});

export const GraduationCapIcon = /* @__PURE__ */ createIcon('graduation-cap', {
  body: ['M2.5 9 12 4.5 21.5 9 12 13.5z'],
  line: [
    'M6.5 11v4.8c0 .6.3 1.1.8 1.4 1.4.8 3 1.3 4.7 1.3s3.3-.5 4.7-1.3c.5-.3.8-.8.8-1.4V11M21.5 9v5',
  ],
});

export const BriefcaseIcon = /* @__PURE__ */ createIcon('briefcase', {
  body: [rect(2.5, 7, 19, 13.5, 3)],
  detail: ['M2.5 12.5c5.9 2.4 13.1 2.4 19 0M12 12.5v2'],
  line: ['M8.5 7V5.5c0-1.1.9-2 2-2h3c1.1 0 2 .9 2 2V7'],
});

// ── Безопасность ────────────────────────────────────────────────────────

export const EyeIcon = /* @__PURE__ */ createIcon('eye', {
  body: [
    'M2.8 12.9c-.4-.6-.4-1.3 0-1.8C4.6 8.4 8 5.5 12 5.5s7.4 2.9 9.2 5.6c.4.6.4 1.3 0 1.8-1.8 2.7-5.2 5.6-9.2 5.6s-7.4-2.9-9.2-5.6z',
  ],
  detail: [circle(12, 12, 3)],
});

export const LockIcon = /* @__PURE__ */ createIcon('lock', {
  body: [rect(4, 10, 16, 11.5, 3)],
  detail: ['M12 14.5V17'],
  line: ['M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10'],
});

export const ShieldIcon = /* @__PURE__ */ createIcon('shield', { body: [shield] });

export const ShieldCheckIcon = /* @__PURE__ */ createIcon('shield-check', {
  body: [shield],
  detail: ['M9 12l2 2 4-4'],
});

// ── Интерфейс ───────────────────────────────────────────────────────────

export const SettingsIcon = /* @__PURE__ */ createIcon('settings', {
  body: [
    'M10.3 2.5h3.4l.5 2.4 1.9 1.1 2.3-.8 1.7 2.9-1.8 1.6v4.6l1.8 1.6-1.7 2.9-2.3-.8-1.9 1.1-.5 2.4h-3.4l-.5-2.4-1.9-1.1-2.3.8-1.7-2.9 1.8-1.6V9.7L3.9 8.1l1.7-2.9 2.3.8 1.9-1.1z',
  ],
  detail: [hole(circle(12, 12, 3))],
});

export const WrenchIcon = /* @__PURE__ */ createIcon('wrench', {
  body: [
    path(
      'M10.5 3v3.5a1.5 1.5 0 0 0 3 0V3A4.5 4.5 0 0 1 13.5 11.4v8.1a1.5 1.5 0 0 1-3 0v-8.1A4.5 4.5 0 0 1 10.5 3z',
      'rotate(45 12 12)'
    ),
  ],
});

export const BulbIcon = /* @__PURE__ */ createIcon('bulb', {
  body: [
    'M8.5 17.5v-1.3c0-.7-.4-1.4-.9-1.9A6.5 6.5 0 1 1 16.4 14.3c-.5.5-.9 1.2-.9 1.9v1.3z',
  ],
  detail: ['M10.5 12.5 12 14l1.5-1.5M12 14v3.5'],
  line: ['M9.5 20.5h5'],
});

export const FilterIcon = /* @__PURE__ */ createIcon('filter', {
  body: [
    'M5 3.5h14c.9 0 1.4 1 .8 1.7L14 12v6.6c0 .4-.2.7-.5.9l-2.5 1.6c-.5.3-1-.1-1-.6V12L4.2 5.2c-.6-.7-.1-1.7.8-1.7z',
  ],
});

export const SlidersIcon = /* @__PURE__ */ createIcon('sliders', {
  body: [circle(14.5, 7, 2.5), circle(9.5, 17, 2.5)],
  line: ['M3 7h9M17 7h4M3 17h4M12 17h9'],
});

export const SortIcon = /* @__PURE__ */ createIcon('sort', {
  line: ['M3.5 6h9M3.5 12h6M3.5 18h4M17.5 19V5M14.5 8l3-3 3 3'],
});

export const ListIcon = /* @__PURE__ */ createIcon('list', {
  line: ['M8.5 6h12M8.5 12h12M8.5 18h12', dot(4, 6), dot(4, 12), dot(4, 18)],
});

export const RowsIcon = /* @__PURE__ */ createIcon('rows', {
  body: [rect(3, 4, 18, 7, 2.5), rect(3, 13, 18, 7, 2.5)],
});

export const GridIcon = /* @__PURE__ */ createIcon('grid', {
  body: [rect(3, 3, 8, 8, 2.5), rect(13, 3, 8, 8, 2.5), rect(3, 13, 8, 8, 2.5), rect(13, 13, 8, 8, 2.5)],
});

export const ArticleIcon = /* @__PURE__ */ createIcon('article', {
  body: [rect(4, 2.5, 16, 19, 3)],
  detail: ['M8 7.5h8M8 11.5h8M8 15.5h5'],
});

export const FileTextIcon = /* @__PURE__ */ createIcon('file-text', {
  body: ['M14 2.5H7.5c-1.7 0-3 1.3-3 3v13c0 1.7 1.3 3 3 3h9c1.7 0 3-1.3 3-3V8z'],
  detail: ['M14 2.5V6c0 1.1.9 2 2 2h3.5M8.5 13h7M8.5 17h4'],
});

export const CopyIcon = /* @__PURE__ */ createIcon('copy', {
  body: [rect(8.5, 8.5, 13, 13, 3)],
  line: ['M15.5 8.5V6c0-1.9-1.6-3.5-3.5-3.5H6C4.1 2.5 2.5 4.1 2.5 6v6c0 1.9 1.6 3.5 3.5 3.5h2.5'],
});

export const PencilIcon = /* @__PURE__ */ createIcon('pencil', {
  body: [
    'M16.2 3.9c.9-.9 2.4-.9 3.3 0l.6.6c.9.9.9 2.4 0 3.3L8.6 19.3c-.3.3-.6.5-1 .6l-3.8 1c-.5.1-.9-.3-.8-.8l1-3.8c.1-.4.3-.7.6-1z',
  ],
  detail: ['M14 6l4 4'],
});

export const TrashIcon = /* @__PURE__ */ createIcon('trash', {
  body: ['M5.5 6.5h13l-.8 12.3c-.1 1.5-1.4 2.7-3 2.7H9.3c-1.6 0-2.9-1.2-3-2.7z'],
  detail: ['M10 10.5V17M14 10.5V17'],
  line: ['M3.5 6.5h17M9 6.5l.6-2.2c.2-.8.9-1.3 1.7-1.3h1.4c.8 0 1.5.5 1.7 1.3l.6 2.2'],
});

export const ShareIcon = /* @__PURE__ */ createIcon('share', {
  body: [circle(18, 5.5, 2.5), circle(6, 12, 2.5), circle(18, 18.5, 2.5)],
  line: ['M8.2 10.8l7.6-4.1M8.2 13.2l7.6 4.1'],
});

export const LogoutIcon = /* @__PURE__ */ createIcon('logout', {
  line: ['M9 4.5H7c-1.7 0-3 1.3-3 3v9c0 1.7 1.3 3 3 3h2M20 12H10M16.5 8l4 4-4 4'],
});

export const PlayIcon = /* @__PURE__ */ createIcon('play', {
  body: ['M7 5.3c0-1.2 1.3-1.9 2.3-1.3l10.2 6.7c.9.6.9 2 0 2.6L9.3 20c-1 .6-2.3-.1-2.3-1.3z'],
});

export const PauseIcon = /* @__PURE__ */ createIcon('pause', {
  body: [rect(5.5, 4, 4.5, 16, 1.5), rect(14, 4, 4.5, 16, 1.5)],
});

export const TrendUpIcon = /* @__PURE__ */ createIcon('trend-up', {
  line: ['M3 17.5l5.5-5.5 4 4 8-8M15.5 7.5h5v5'],
});

export const TargetIcon = /* @__PURE__ */ createIcon('target', {
  body: [circle(12, 12, 9.5)],
  detail: [circle(12, 12, 5.5), circle(12, 12, 2)],
});

export const RocketIcon = /* @__PURE__ */ createIcon('rocket', {
  body: ['M12.3 16.8 7.2 11.7C8.7 7.1 12.6 3.4 20.6 3.4c0 8-3.7 11.9-8.3 13.4z'],
  detail: [circle(15, 9, 1.8)],
  line: [
    'M7.2 11.7H4.5c-.7 0-1.1-.8-.7-1.4l1.6-2.5c.4-.6 1-.9 1.7-.9H10M12.3 16.8v2.7c0 .7.8 1.1 1.4.7l2.5-1.6c.6-.4.9-1 .9-1.7V14M6 15.5c-1.5.5-2.5 2-2.5 5 3 0 4.5-1 5-2.5',
  ],
});

// ── Устройства и медиа ──────────────────────────────────────────────────

export const MonitorIcon = /* @__PURE__ */ createIcon('monitor', {
  body: [rect(2.5, 3.5, 19, 13, 3)],
  line: ['M12 16.5v4M8 20.5h8'],
});

export const SmartphoneIcon = /* @__PURE__ */ createIcon('smartphone', {
  body: [rect(5.5, 2, 13, 20, 3)],
  detail: ['M11 18.5h2'],
});

export const CameraIcon = /* @__PURE__ */ createIcon('camera', {
  body: [
    'M3 9c0-1.4 1.1-2.5 2.5-2.5h1.8c.6 0 1.1-.3 1.4-.8l.7-1.3c.3-.5.8-.9 1.4-.9h2.4c.6 0 1.1.4 1.4.9l.7 1.3c.3.5.8.8 1.4.8h1.8C19.9 6.5 21 7.6 21 9v9c0 1.4-1.1 2.5-2.5 2.5h-13C4.1 20.5 3 19.4 3 18z',
  ],
  detail: [circle(12, 13, 3.5)],
});

export const ImageIcon = /* @__PURE__ */ createIcon('image', {
  body: [rect(3, 3, 18, 18, 4)],
  detail: [
    circle(9, 9, 1.8),
    'M3 16.5l4.5-4.2c.8-.7 2-.7 2.8 0L15 17M13.5 15.5l1.8-1.6c.8-.7 2-.7 2.7 0L21 16.5',
  ],
});

export const GlobeIcon = /* @__PURE__ */ createIcon('globe', {
  body: [circle(12, 12, 9.5)],
  detail: [
    'M2.5 12h19M12 2.5c-2.5 2.6-3.8 5.8-3.8 9.5s1.3 6.9 3.8 9.5M12 2.5c2.5 2.6 3.8 5.8 3.8 9.5s-1.3 6.9-3.8 9.5',
  ],
});
